/**
 * The spreadsheet's data model, its wire formats, and a small formula engine.
 *
 * Kept apart from the component so the rules that decide what a cell *is* can
 * be read — and tested — without a DOM. Two wire formats are supported:
 *
 *   .xlsx / .xlsm  the rich envelope from backend/modules/vault/services/
 *                  vault_office_format.py, which carries formatting per cell
 *   .csv / .tsv    plain delimited text, which carries values only
 */

export interface Cell {
  v?: string;
  b?: 1;
  i?: 1;
  u?: 1;
  fg?: string;
  bg?: string;
  a?: 'left' | 'center' | 'right';
  sz?: number;
}

export interface Sheet {
  name: string;
  orig?: string;
  rows: (Cell | null)[][];
  widths?: (number | null)[];
  merges?: string[];
}

export interface Book {
  sheets: Sheet[];
  /** csv carries no formatting; the editor hides the formatting controls. */
  kind: 'xlsx' | 'csv';
}

export const MIN_ROWS = 12;
export const MIN_COLS = 8;

/** A, B, … Z, AA, AB — spreadsheet column names. */
export function colName(index: number): string {
  let n = index;
  let s = '';
  do {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return s;
}

export function colIndex(name: string): number {
  let n = 0;
  for (const ch of name.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

/** Pad a sheet out so there is always somewhere to type. */
function padded(rows: (Cell | null)[][]): (Cell | null)[][] {
  const width = Math.max(MIN_COLS, ...rows.map((r) => r.length), 1);
  const out = rows.map((r) => {
    const copy = r.slice();
    while (copy.length < width) copy.push(null);
    return copy;
  });
  while (out.length < MIN_ROWS) out.push(new Array(width).fill(null));
  return out;
}

// ---------------------------------------------------------------- CSV

/** Split one CSV line, honouring quotes and embedded commas/newlines. */
export function parseDelimited(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i += 1; } else quoted = false;
      } else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === delimiter) { row.push(field); field = ''; }
    else if (ch === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
    else if (ch !== '\r') field += ch;
  }
  row.push(field);
  rows.push(row);
  // A file ending in a newline should not gain a phantom last row.
  if (rows.length > 1 && rows[rows.length - 1].every((f) => f === '')) rows.pop();
  return rows;
}

function toDelimited(rows: (Cell | null)[][], delimiter: string): string {
  return rows
    .map((row) =>
      row
        .map((c) => {
          const v = c?.v ?? '';
          return /["\n\r]|^\s|\s$/.test(v) || v.includes(delimiter)
            ? `"${v.replace(/"/g, '""')}"`
            : v;
        })
        .join(delimiter),
    )
    .join('\n');
}

// ---------------------------------------------------------------- parse / serialise

export function parseBook(value: string, kind: 'xlsx' | 'csv', delimiter = ','): Book {
  if (kind === 'csv') {
    const rows = parseDelimited(value ?? '', delimiter).map((r) =>
      r.map((v) => (v === '' ? null : ({ v } as Cell))),
    );
    return { kind, sheets: [{ name: 'Sheet1', rows: padded(rows) }] };
  }
  try {
    const parsed = JSON.parse(value);
    if (parsed && Array.isArray(parsed.sheets)) {
      return {
        kind,
        sheets: parsed.sheets.map((s: any) => ({
          name: s.name || 'Sheet1',
          orig: s.orig,
          widths: s.widths,
          merges: s.merges,
          rows: padded((s.rows || []).map((r: any[]) => (r || []).map((c) => c || null))),
        })),
      };
    }
  } catch {
    /* falls through to the legacy tab-separated form below */
  }
  const SHEET_MARK = '### SHEET: ';
  const sheets: Sheet[] = [];
  let current: Sheet | null = null;
  for (const line of (value ?? '').split('\n')) {
    if (line.startsWith(SHEET_MARK)) {
      current = { name: line.slice(SHEET_MARK.length).trim(), rows: [] };
      current.orig = current.name;
      sheets.push(current);
    } else if (current) {
      current.rows.push(line.split('\t').map((v) => (v === '' ? null : ({ v } as Cell))));
    }
  }
  const out = sheets.length ? sheets : [{ name: 'Sheet1', orig: 'Sheet1', rows: [[]] }];
  return { kind, sheets: out.map((s) => ({ ...s, rows: padded(s.rows) })) };
}

/** Drop the padding we added, so an untouched file serialises back unchanged. */
function trimmed(rows: (Cell | null)[][]): (Cell | null)[][] {
  const used = (c: Cell | null) => c && (c.v || c.b || c.i || c.u || c.fg || c.bg || c.a || c.sz);
  let lastRow = -1;
  let lastCol = -1;
  rows.forEach((row, r) => {
    row.forEach((cell, c) => {
      if (used(cell)) {
        lastRow = Math.max(lastRow, r);
        lastCol = Math.max(lastCol, c);
      }
    });
  });
  if (lastRow < 0) return [[null]];
  return rows.slice(0, lastRow + 1).map((r) => r.slice(0, lastCol + 1));
}

export function serialiseBook(book: Book, delimiter = ','): string {
  if (book.kind === 'csv') return toDelimited(trimmed(book.sheets[0].rows), delimiter);
  return JSON.stringify({
    vault_sheet: 1,
    sheets: book.sheets.map((s) => ({
      name: s.name,
      orig: s.orig,
      widths: s.widths,
      merges: s.merges,
      rows: trimmed(s.rows),
    })),
  });
}

// ---------------------------------------------------------------- formulas

const FUNCTIONS: Record<string, (nums: number[]) => number> = {
  SUM: (n) => n.reduce((a, b) => a + b, 0),
  AVERAGE: (n) => (n.length ? n.reduce((a, b) => a + b, 0) / n.length : 0),
  MIN: (n) => (n.length ? Math.min(...n) : 0),
  MAX: (n) => (n.length ? Math.max(...n) : 0),
  COUNT: (n) => n.length,
  ROUND: (n) => (n.length > 1 ? Number(n[0].toFixed(Math.max(0, n[1]))) : Math.round(n[0] ?? 0)),
  ABS: (n) => Math.abs(n[0] ?? 0),
  PRODUCT: (n) => n.reduce((a, b) => a * b, 1),
};

export const FUNCTION_NAMES = Object.keys(FUNCTIONS);

const REF = /^\$?([A-Za-z]{1,3})\$?(\d{1,7})/;

/**
 * Evaluate a cell's displayed value. Non-formulas come back verbatim; a
 * formula is computed against the sheet, with a recursion guard so a cell that
 * refers to itself reports #CIRC! rather than hanging the browser.
 */
export function displayValue(cell: Cell | null, rows: (Cell | null)[][], depth = 0): string {
  const raw = cell?.v ?? '';
  if (!raw.startsWith('=')) return raw;
  if (depth > 12) return '#CIRC!';
  try {
    const n = evaluate(raw.slice(1), rows, depth);
    if (n === null) return '#ERROR!';
    return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(10)));
  } catch {
    return '#ERROR!';
  }
}

function cellNumber(rows: (Cell | null)[][], r: number, c: number, depth: number): number {
  const cell = rows[r]?.[c] ?? null;
  const text = displayValue(cell, rows, depth + 1);
  const n = Number(text);
  return Number.isFinite(n) ? n : 0;
}

function rangeNumbers(rows: (Cell | null)[][], a: string, b: string, depth: number): number[] {
  const ma = REF.exec(a);
  const mb = REF.exec(b);
  if (!ma || !mb) return [];
  const c1 = colIndex(ma[1]);
  const r1 = Number(ma[2]) - 1;
  const c2 = colIndex(mb[1]);
  const r2 = Number(mb[2]) - 1;
  const out: number[] = [];
  for (let r = Math.min(r1, r2); r <= Math.max(r1, r2); r += 1) {
    for (let c = Math.min(c1, c2); c <= Math.max(c1, c2); c += 1) {
      const cell = rows[r]?.[c];
      if (cell?.v !== undefined && cell.v !== '') out.push(cellNumber(rows, r, c, depth));
    }
  }
  return out;
}

/** A tiny recursive-descent evaluator — no eval(), no injected code. */
function evaluate(expr: string, rows: (Cell | null)[][], depth: number): number | null {
  let i = 0;
  const src = expr;

  const skip = () => { while (i < src.length && src[i] === ' ') i += 1; };

  const parsePrimary = (): number => {
    skip();
    if (src[i] === '(') { i += 1; const v = parseExpr(); skip(); if (src[i] === ')') i += 1; return v; }
    if (src[i] === '-') { i += 1; return -parsePrimary(); }
    if (src[i] === '+') { i += 1; return parsePrimary(); }

    const name = /^[A-Za-z]+(?=\s*\()/.exec(src.slice(i));
    if (name) {
      const fn = FUNCTIONS[name[0].toUpperCase()];
      i += name[0].length;
      skip();
      i += 1; // '('
      const args: number[] = [];
      for (;;) {
        skip();
        if (src[i] === ')') { i += 1; break; }
        // A range argument (A1:B4) contributes every value inside it.
        const range = /^\$?[A-Za-z]{1,3}\$?\d{1,7}\s*:\s*\$?[A-Za-z]{1,3}\$?\d{1,7}/.exec(src.slice(i));
        if (range) {
          const [from, to] = range[0].split(':');
          args.push(...rangeNumbers(rows, from.trim(), to.trim(), depth));
          i += range[0].length;
        } else {
          args.push(parseExpr());
        }
        skip();
        if (src[i] === ',' || src[i] === ';') i += 1;
        else if (src[i] === ')') { i += 1; break; }
        else if (i >= src.length) break;
      }
      if (!fn) throw new Error('unknown function');
      return fn(args);
    }

    const ref = REF.exec(src.slice(i));
    if (ref) {
      i += ref[0].length;
      return cellNumber(rows, Number(ref[2]) - 1, colIndex(ref[1]), depth);
    }

    const num = /^\d*\.?\d+(e[+-]?\d+)?/i.exec(src.slice(i));
    if (num) { i += num[0].length; return Number(num[0]); }
    throw new Error('unparsable');
  };

  const parsePower = (): number => {
    let left = parsePrimary();
    for (;;) {
      skip();
      if (src[i] === '^') { i += 1; left **= parsePrimary(); } else return left;
    }
  };

  const parseTerm = (): number => {
    let left = parsePower();
    for (;;) {
      skip();
      if (src[i] === '*') { i += 1; left *= parsePower(); }
      else if (src[i] === '/') { i += 1; const d = parsePower(); if (d === 0) throw new Error('div0'); left /= d; }
      else if (src[i] === '%') { i += 1; left /= 100; }
      else return left;
    }
  };

  const parseExpr = (): number => {
    let left = parseTerm();
    for (;;) {
      skip();
      if (src[i] === '+') { i += 1; left += parseTerm(); }
      else if (src[i] === '-') { i += 1; left -= parseTerm(); }
      else return left;
    }
  };

  const value = parseExpr();
  skip();
  if (i < src.length) return null;
  return Number.isFinite(value) ? value : null;
}
